/**
 * Viewport and interaction state for the canvas: zoom, pan, active tool, snap
 * guides, marquee, drag flags.
 *
 * Deliberately separate from `DesignDocumentStore` — none of this is ever
 * serialised, and keeping it apart means panning does not push an undo entry.
 */

import { Injectable, computed, signal } from '@angular/core';

import { CANVAS_EDGE_MARGIN, ZOOM_MAX, ZOOM_MIN, ZOOM_STEP } from '../design-builder.constants';
import type {
  AlignmentGuide,
  DesignTool,
  MarqueeRect,
  Point,
  Rect,
} from '../design-builder.types';
import {
  type Viewport,
  clamp,
  fitViewport,
  toDocumentPoint,
  toScreenPoint,
  zoomAt,
} from '../utils/design-geometry.utils';

/** What the pointer is currently doing on the canvas. */
export type CanvasGesture = 'idle' | 'move' | 'resize' | 'rotate' | 'marquee' | 'pan' | 'draw';

@Injectable()
export class DesignCanvasStore {
  private readonly _zoom = signal(1);
  private readonly _panX = signal(0);
  private readonly _panY = signal(0);
  private readonly _tool = signal<DesignTool>('select');
  private readonly _gesture = signal<CanvasGesture>('idle');
  private readonly _guides = signal<AlignmentGuide[]>([]);
  private readonly _marquee = signal<MarqueeRect | null>(null);
  private readonly _spaceHeld = signal(false);
  /** Frame the pointer is hovering during a palette drag — the drop target. */
  private readonly _dropTargetId = signal<string | null>(null);
  /** Node being text-edited in place. */
  private readonly _editingTextId = signal<string | null>(null);
  private readonly _viewportSize = signal({ width: 0, height: 0 });
  /**
   * Layout grid drawn ON TOP of the frames and widgets, for lining things up.
   * View-only (like zoom), so toggling it never dirties the design or pushes
   * an undo entry. The workspace grid behind the frames is `canvas.grid`.
   */
  private readonly _layoutGrid = signal(false);
  readonly layoutGrid = this._layoutGrid.asReadonly();

  setLayoutGrid(visible: boolean): void {
    this._layoutGrid.set(visible);
  }

  /**
   * True only once a gesture has actually MOVED past the drag threshold.
   *
   * Distinct from `gesture !== 'idle'`, which flips on pointerDOWN — so a plain
   * click counted as "interacting" and swapped every live widget for a
   * placeholder and back, destroying and recreating the component. Charts and
   * tables that render from a `setTimeout` in `ngAfterViewInit` could come back
   * blank. Only a real drag suspends them now.
   */
  private readonly _dragging = signal(false);
  readonly dragging = this._dragging.asReadonly();

  setDragging(dragging: boolean): void {
    if (this._dragging() !== dragging) this._dragging.set(dragging);
  }

  readonly zoom = this._zoom.asReadonly();
  readonly panX = this._panX.asReadonly();
  readonly panY = this._panY.asReadonly();
  readonly tool = this._tool.asReadonly();
  readonly gesture = this._gesture.asReadonly();
  readonly guides = this._guides.asReadonly();
  readonly marquee = this._marquee.asReadonly();
  readonly spaceHeld = this._spaceHeld.asReadonly();
  readonly dropTargetId = this._dropTargetId.asReadonly();
  readonly editingTextId = this._editingTextId.asReadonly();

  readonly viewport = computed<Viewport>(() => ({
    zoom: this._zoom(),
    panX: this._panX(),
    panY: this._panY(),
  }));

  readonly zoomPercent = computed(() => Math.round(this._zoom() * 100));

  /** The transform applied to the artboard layer. */
  readonly canvasTransform = computed(
    () => `translate(${this._panX()}px, ${this._panY()}px) scale(${this._zoom()})`,
  );

  /** True while the canvas should show a grab cursor and swallow node clicks. */
  readonly isPanMode = computed(() => this._tool() === 'hand' || this._spaceHeld());

  /** Node interactions are suppressed mid-gesture and while panning. */
  readonly isInteracting = computed(() => this._gesture() !== 'idle');

  // ── Tool ───────────────────────────────────────────────────────────────────

  setTool(tool: DesignTool): void {
    this._tool.set(tool);
  }

  /** Back to select — what every draw/insert falls back to once it completes. */
  resetTool(): void {
    this._tool.set('select');
  }

  setSpaceHeld(held: boolean): void {
    this._spaceHeld.set(held);
  }

  // ── Gesture ────────────────────────────────────────────────────────────────

  setGesture(gesture: CanvasGesture): void {
    this._gesture.set(gesture);
    if (gesture === 'idle') {
      this._guides.set([]);
      this._dragging.set(false);
    }
  }

  setGuides(guides: AlignmentGuide[]): void {
    this._guides.set(guides);
  }

  clearGuides(): void {
    if (this._guides().length) this._guides.set([]);
  }

  setMarquee(marquee: MarqueeRect | null): void {
    this._marquee.set(marquee);
  }

  setDropTarget(id: string | null): void {
    if (this._dropTargetId() !== id) this._dropTargetId.set(id);
  }

  setEditingText(id: string | null): void {
    this._editingTextId.set(id);
  }

  // ── Viewport ───────────────────────────────────────────────────────────────

  setViewportSize(width: number, height: number): void {
    this._viewportSize.set({ width, height });
    // Still on the fitted view: follow the pane (a panel opening / closing,
    // leaving Preview, the window resizing) so the page keeps filling it.
    if (this.autoFit) this.fitToWidth();
  }

  /**
   * True while the view is the fitted default. Fitting at open time alone
   * could catch the pane mid-layout (side panels animate their width), which
   * left the page shrunk to a fraction of the canvas. Any zoom or pan by the
   * user ends it, so the view never jumps under them.
   */
  private autoFit = false;

  /** Artboard size in document px — drives pan clamping. */
  private readonly _contentSize = signal({ width: 0, height: 0 });

  setContentSize(width: number, height: number): void {
    const current = this._contentSize();
    if (current.width === width && current.height === height) return;
    this._contentSize.set({ width, height });
    // The fitted view follows a new page size (e.g. a template just opened).
    if (this.autoFit) { this.fitToWidth(); return; }
    // Re-clamp: a smaller artboard may have left the pan out of bounds.
    this.setViewport(this.viewport());
  }

  setViewport(viewport: Viewport): void {
    const clamped = this.clampViewport({
      ...viewport,
      zoom: clamp(viewport.zoom, ZOOM_MIN, ZOOM_MAX),
    });
    this._zoom.set(clamped.zoom);
    this._panX.set(clamped.panX);
    this._panY.set(clamped.panY);
  }

  /**
   * Keep the artboard pinned like a page rather than letting it drift off into
   * empty space. On an axis where the scaled artboard FITS the viewport it is
   * centred and the pan is fixed; where it overflows, the pan is bounded so an
   * edge can never be dragged past the viewport edge (plus a small margin).
   */
  private clampViewport(viewport: Viewport): Viewport {
    const content = this._contentSize();
    const size = this._viewportSize();
    if (!content.width || !content.height || !size.width || !size.height) return viewport;

    const margin = CANVAS_EDGE_MARGIN;
    const axis = (
      scaled: number,
      available: number,
      pan: number,
    ): number => {
      if (scaled + margin * 2 <= available) return (available - scaled) / 2;
      return clamp(pan, available - scaled - margin, margin);
    };

    return {
      zoom: viewport.zoom,
      panX: axis(content.width * viewport.zoom, size.width, viewport.panX),
      panY: axis(content.height * viewport.zoom, size.height, viewport.panY),
    };
  }

  panBy(dx: number, dy: number): void {
    this.autoFit = false;
    this._panX.update((value) => value + dx);
    this._panY.update((value) => value + dy);
  }

  /** Zoom keeping `anchor` (screen px, relative to the viewport) under cursor. */
  zoomBy(factor: number, anchor: Point): void {
    this.autoFit = false;
    const next = clamp(this._zoom() * factor, ZOOM_MIN, ZOOM_MAX);
    if (next === this._zoom()) return;
    this.setViewport(zoomAt(this.viewport(), next, anchor));
  }

  zoomIn(anchor?: Point): void {
    this.zoomBy(ZOOM_STEP, anchor ?? this.viewportCenter());
  }

  zoomOut(anchor?: Point): void {
    this.zoomBy(1 / ZOOM_STEP, anchor ?? this.viewportCenter());
  }

  /** Exact zoom about the viewport centre — the toolbar's 100% button. */
  setZoom(zoom: number): void {
    this.autoFit = false;
    const next = clamp(zoom, ZOOM_MIN, ZOOM_MAX);
    this.setViewport(zoomAt(this.viewport(), next, this.viewportCenter()));
  }

  /** Fit a rect (the artboard, or the selection) into the visible area. */
  fitTo(content: Rect, margin = CANVAS_EDGE_MARGIN): void {
    this.autoFit = false;
    const size = this._viewportSize();
    if (!size.width || !size.height) return;
    const fitted = fitViewport(content, size, margin);
    this.setViewport({ ...fitted, zoom: clamp(fitted.zoom, ZOOM_MIN, ZOOM_MAX) });
  }

  /**
   * Fit the artboard's WIDTH and pin it to the top — the default view, so the
   * canvas reads as a full page rather than a small card floating in grey.
   * Taller pages then simply scroll, the way a document does.
   */
  fitToWidth(): void {
    const content = this._contentSize();
    const size = this._viewportSize();
    if (!content.width || !size.width) return;

    const zoom = clamp(
      (size.width - CANVAS_EDGE_MARGIN * 2) / content.width,
      ZOOM_MIN,
      // Never blow a small artboard up past 1:1 — that would look broken.
      1,
    );
    this.setViewport({ zoom, panX: 0, panY: CANVAS_EDGE_MARGIN });
    this.autoFit = true;
  }

  /** A pan the user dragged — leaves the fitted view. */
  panTo(panX: number, panY: number): void {
    this.autoFit = false;
    this.setViewport({ zoom: this._zoom(), panX, panY });
  }

  private viewportCenter(): Point {
    const size = this._viewportSize();
    return { x: size.width / 2, y: size.height / 2 };
  }

  // ── Conversion helpers (so components never import geometry directly) ──────

  toDocument(screen: Point): Point {
    return toDocumentPoint(screen, this.viewport());
  }

  toScreen(doc: Point): Point {
    return toScreenPoint(doc, this.viewport());
  }

  /** Reset everything a new document should not inherit. */
  reset(): void {
    this._tool.set('select');
    this._gesture.set('idle');
    this._guides.set([]);
    this._marquee.set(null);
    this._dropTargetId.set(null);
    this._editingTextId.set(null);
  }
}
